import { useSelector } from 'react-redux';
import classNames from 'classnames/bind';
import { Label } from '@schibsted-svp/react-ui';
import type { RootState } from 'store';
import { getEncoderStatus } from 'store/encoder-status/selectors';
import Tooltip from 'components/ui/Tooltip';
import { EncoderStatusIndicator } from './EncoderStatusIndicator';
import css from './EncoderStatus.module.scss';

const cln = classNames.bind(css);

type EncoderStatusProps = {
    assetType: string;
    label?: string;
    className?: string;
};

export function EncoderStatus({ assetType, label, className }: EncoderStatusProps) {
    const { messages, messagesRate, currentReplicas, desiredReplicas } = useSelector((state: RootState) =>
        getEncoderStatus(state, { assetType })
    );

    const messagesRatePerMinute = Math.round(messagesRate * 60);

    const isScaling = desiredReplicas > currentReplicas;
    const indicatorStatus = isScaling ? 'scaling' : 'optimal';

    const tooltipContent = (
        <div className={css.tooltipContainer}>
            <div className={css.tooltipHeader}>
                Encoders details
                <EncoderStatusIndicator variant="icon" status={indicatorStatus} />
            </div>
            <div className={css.tooltipItems}>
                <div className={css.tooltipItem}>
                    <span>{messages}</span> {messages === 1 ? 'file' : 'files'} in queue
                </div>
                <div className={css.tooltipItem}>
                    <span>{messagesRatePerMinute}</span> {messagesRatePerMinute === 1 ? 'file' : 'files'} per minute
                </div>
                <div className={css.tooltipItem}>
                    <span>{currentReplicas}</span> {currentReplicas === 1 ? 'encoder' : 'encoders'} running
                </div>
                {isScaling && (
                    <div className={css.tooltipItem}>
                        <span className={css.highlighted}>{desiredReplicas}</span>{' '}
                        {desiredReplicas === 1 ? 'encoder' : 'encoders'} target
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div className={cln('container', className)}>
            <Label className={css.label}>{label ?? 'Encoders status:'}</Label>
            <Tooltip placement="top" theme="light" offset={[0, 15]} content={tooltipContent}>
                <div>
                    <EncoderStatusIndicator variant="badge" status={indicatorStatus} />
                </div>
            </Tooltip>
        </div>
    );
}
